import * as React from 'react';

import { Messages } from '../../../codegen/Localize';
import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { DataSources } from '../../../ts/resources/Datasources';
import { ServerService } from '../../../ts/services/ServerService';
import { ServiceLocator } from '../../ServiceLocator';
import { getCommonDatasourceInfo } from '../CommonDatasourceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { DatasourceTooltip } from '../Tooltips/DatasourceTooltip';
import { TooltipAnchor } from '../Tooltips/TooltipAnchor';
import { CardViewStyles } from './CardViewStyles';
import { ConnectedDetailedDatasourceThumbnail } from './ConnectedDetailedDatasourceThumbnail';
import { ContentCard, getContentUrlForItem } from './ContentCard';
import { DetailsTooltipStyles } from './DetailsTooltipStyles';
import { Thumbnail } from './Thumbnail';

export class DatasourceCard extends ContentCard {

  private isUnifiedDatasource(): boolean {
    return DataSources.isUnifiedDatasource(this.props.content);
  }

  private getDatasourceId(): string{
    if(this.isUnifiedDatasource()){
      return (this.props.content as IDataSourceInfo).idForFavoriting;
    }
    else {
      return this.props.content.id;
    }
  }

  private hasExtraDatasourceDetail(): boolean{
    return this.isUnifiedDatasource() || this.props.content.contentDetail;
  }

  public render() {
    let thumbnailModel = null;
    if (this.isUnifiedDatasource()) {
      thumbnailModel = getCommonDatasourceInfo(this.props.content as IDataSourceInfo);
    } else if (this.props.content.contentDetail) {
      thumbnailModel = getCommonDatasourceInfo(this.props.content as IContentInfo);
    }

    const contentUrl = getDatasourceContentUrl(this.props.urls, this.props.content, this.props.inWorkbookDatasourcesSubspace);

    let thumbnail: JSX.Element = null;
    if (this.hasExtraDatasourceDetail()) {
      thumbnail = <ConnectedDetailedDatasourceThumbnail urls={this.props.urls}
                                               contentUrl={contentUrl}
                                               hoveredOrSelected={this.props.hoveredOrSelected}
                                               datasource={thumbnailModel}
                                               showExtractEncryptionStatus={this.showExtractEncryptionStatus()} />;
    } else {
      thumbnail = <Thumbnail navigateUrl={contentUrl}
                             defaultImageUrl={imgPath('data-source-icon-loading.svg')}
                             hoveredOrSelected={this.props.hoveredOrSelected} />;
    }

    const commonDatasource = getCommonDatasourceInfo(this.props.content);

    return (
      <div className={CardViewStyles.Card}>
        <TooltipAnchor tooltipContent={this.renderTooltipContent}
                       width={DetailsTooltipStyles.Width}
                       onShowTooltip={this.handleShowTooltip}
                       disableAutoFocus={DataSources.isEmbedded(commonDatasource) || (this.props.content.owner && this.props.content.owner.isHiddenInternalUser)}
                       title={Messages.datasourceDetails()}>
            { thumbnail }
        </TooltipAnchor>
        <div className={CardViewStyles.ActionRow}>
          <span className={CardViewStyles.ActionRowName} title={this.props.content.name}>
            { this.props.content.name }
          </span>
          {!DataSources.isEmbedded(commonDatasource) &&
            <span>
              <FavoriteToggle canHaveKeyboardFocus={true}
                              isFavorite={this.props.content.favorite}
                              onAddFavorite={() => this.props.addContentFavorite(this.getDatasourceId(), 'datasource')}
                              onRemoveFavorite={() => this.props.removeContentFavorite(this.props.content.id, this.getDatasourceId(), 'datasource', this.props.content.name)}/>
            </span>
          }
          { this.renderActionsMenu() }
        </div>
        <div className={CardViewStyles.CardDescription}>
          {this.getDescription()}
        </div>
      </div>
    );
  }

  private renderTooltipContent = () => {
    return <DatasourceTooltip content={this.props.content}
                              urls={this.props.urls}
                              inProjectSpace={this.props.inProjectSpace}/>;
  }

  private showExtractEncryptionStatus() : boolean {
    const serverService = ServiceLocator.get<ServerService>('ServerService');
    return serverService.isExtractEncryptionEnabledOmniSwitch();
  }
}

export function getDatasourceContentUrl(
    urls: SpacesSiteUrls,
    content: IContentInfo,
    inWorkbookDatasourcesSubspace: boolean
): string {
  const commonDatasource = getCommonDatasourceInfo(content);

  const workbook = DataSources.isEmbedded(commonDatasource) ? commonDatasource.workbook : undefined;
  if (workbook) {
    // If we're already in a workbook place, the card href would link us to where we already are,
    // so don't make it a link.
    if (inWorkbookDatasourcesSubspace) {
      return undefined;
    }

    return urls.workbookDatasourcesUrl(workbook.id);
  }

  if (DataSources.isUnifiedDatasource(content)) {
    return urls.datasourceUrl(content.id);
  }

  return getContentUrlForItem(content, urls);
}
